import { ShieldBan, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { AdminPersonResponse } from "../../features/admin/contracts/AdminPersonResponse";
import { AdminReportExcerptKind } from "../../features/admin/contracts/AdminReportContextMessageResponse";
import { AdminReportResponse } from "../../features/admin/contracts/AdminReportResponse";
import {
  blockReported,
  deleteReportedMessage,
  dismissAdminReport,
} from "../../features/admin/slice-extensions/admin-moderation-slice-extension";
import AdminBlockDialog from "./AdminBlockDialog";

const formatDateTime = (value: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));

interface AdminReportRowProps {
  report: AdminReportResponse;
  /** Called with the handled report so the page can update its list and the badge. */
  onHandled: (report: AdminReportResponse) => void;
}

/** One report: who reported whom, where, why, the excerpt and the last messages, and the three actions. */
const AdminReportRow = ({ report, onHandled }: AdminReportRowProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const open = report.status === "Open";

  const person = (p: AdminPersonResponse | null): string =>
    p ? `${p.name || t("admin.reports.unknownPerson")} (${t(`admin.reports.kind.${p.kind}`)})` : t("admin.reports.nobody");
  const excerpt = (text: string, kind: AdminReportExcerptKind): string =>
    kind === "Text" ? text : t(`admin.reports.excerpt.${kind}`);

  const run = async (action: () => Promise<{ payload?: unknown; type: string }>) => {
    setBusy(true);
    const result = await action();
    setBusy(false);
    if (result.type.endsWith("/rejected")) {
      toast.error(t("admin.reports.actionFailed"));
      return;
    }
    setBlocking(false);
    onHandled(result.payload as AdminReportResponse);
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6" data-testid="admin-report-row">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <p className="font-semibold text-grey-10 dark:text-white">
            {t(`admin.reports.reason.${report.reason}`)} · {report.organizationName}
          </p>
          <p className="text-grey-40 dark:text-grey-70">
            {t("admin.reports.reportedBy")}: {person(report.reporter)} · {t("admin.reports.about")}: {person(report.reported)}
          </p>
          <p className="text-grey-40 dark:text-grey-70">
            {t(`admin.reports.target.${report.targetKind}`)}
            {report.threadKind ? ` · ${t(`admin.reports.thread.${report.threadKind}`)}` : ""} ·{" "}
            {formatDateTime(report.createdAt)}
          </p>
          {report.reportedBlock && (
            <p className="text-red-700 dark:text-red-400">{t("admin.blocks.blocked")}</p>
          )}
        </div>
        <p className="text-xs text-grey-50 dark:text-grey-60">{t(`admin.reports.status.${report.status}`)}</p>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm text-grey-20 dark:text-grey-80">
        {excerpt(report.excerpt, report.excerptKind)}
      </p>
      {report.note && (
        <p className="mt-2 text-sm text-grey-40 dark:text-grey-70">
          {t("admin.reports.note")}: {report.note}
        </p>
      )}
      {report.context.length > 0 && (
        <ol className="mt-3 space-y-1 border-l border-grey-90 pl-3 text-xs dark:border-grey-50">
          {report.context.map((line) => (
            <li key={line.id} className={line.isReported ? "font-semibold text-grey-20 dark:text-white" : "text-grey-40 dark:text-grey-70"}>
              {line.author?.name ?? ""} · {formatDateTime(line.createdAt)}: {excerpt(line.excerpt, line.excerptKind)}
            </li>
          ))}
        </ol>
      )}
      {open && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className="btn btn-secondary flex items-center gap-1.5" disabled={busy} onClick={() => run(() => dispatch(dismissAdminReport(report.id)))}>
            <X size={16} aria-hidden="true" />
            {t("admin.reports.dismiss")}
          </button>
          {report.targetKind === "Message" && report.excerptKind !== "Gone" && (
            <button type="button" className="btn btn-secondary flex items-center gap-1.5" disabled={busy} onClick={() => run(() => dispatch(deleteReportedMessage(report.id)))}>
              <Trash2 size={16} aria-hidden="true" />
              {t("admin.reports.deleteMessage")}
            </button>
          )}
          {report.reported && !report.reportedBlock && (
            <button type="button" className="btn btn-danger flex items-center gap-1.5" disabled={busy} onClick={() => setBlocking(true)}>
              <ShieldBan size={16} aria-hidden="true" />
              {t("admin.blocks.block")}
            </button>
          )}
        </div>
      )}
      {blocking && report.reported && (
        <AdminBlockDialog
          name={report.reported.name}
          busy={busy}
          onClose={() => setBlocking(false)}
          onConfirm={(reason) => run(() => dispatch(blockReported({ reportId: report.id, reason })))}
        />
      )}
    </div>
  );
};

export default AdminReportRow;
